import { useState } from 'react';
import { Input } from '../catalyst/input';
import { FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import { useGoQuery } from '../hooks/useGoQuery';
import { GoResultsList } from './GoResultsList';

export function GoInput() {
  const [value, setValue] = useState('');
  const [submittedValue, setSubmittedValue] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { submit, pick, reset, results, isPending, isError, noResult } = useGoQuery({
    onNavigate: () => {
      setValue('');
      setSubmittedValue(null);
      reset();
    },
  });

  function handleChange(next: string) {
    setValue(next);
    setSubmittedValue(null);
    setSelectedIndex(0);
    reset();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' && results.length > 0) {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
      return;
    }
    if (e.key === 'ArrowUp' && results.length > 0) {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Escape') {
      handleChange(value);
      return;
    }
    if (e.key === 'Enter') {
      const openInNewTab = e.metaKey || e.ctrlKey;
      if (value === submittedValue && results.length > 0) {
        pick(results[selectedIndex], openInNewTab);
        return;
      }
      setSelectedIndex(0);
      setSubmittedValue(value);
      submit(value, openInNewTab);
    }
  }

  return (
    <div className="relative mb-3">
      <Input placeholder="Go..." value={value} onChange={(e) => handleChange(e.target.value)} onKeyDown={handleKeyDown} />

      {isPending && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          <FaSpinner className="text-gray-500 animate-spin" />
        </div>
      )}

      {isError && !isPending && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          <FaExclamationTriangle className="text-red-500" />
        </div>
      )}

      {results.length > 0 && (
        <div className="absolute inset-x-0 top-full z-40 mt-1 overflow-hidden rounded-lg border border-zinc-950/10 bg-white shadow-lg dark:border-white/10 dark:bg-zinc-900">
          <GoResultsList results={results} selectedIndex={selectedIndex} onHover={setSelectedIndex} onPick={pick} />
        </div>
      )}

      {noResult && !isPending && (
        <div className="absolute inset-x-0 top-full z-40 mt-1 overflow-hidden rounded-lg border border-zinc-950/10 bg-white px-3 py-2 text-sm text-zinc-500 shadow-lg dark:border-white/10 dark:bg-zinc-900 dark:text-zinc-400">
          No match for <span className="font-mono text-zinc-900 dark:text-white">{submittedValue}</span>
        </div>
      )}
    </div>
  );
}
